import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("rf_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("rf_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from the Google OAuth callback, skip the /me check —
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const loginWithData = useCallback((data) => {
    localStorage.setItem("rf_token", data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    api.post("/auth/logout").catch(() => {}); // clears the Google session cookie server-side
    localStorage.removeItem("rf_token");
    setUser(false);
  }, []);

  const updateCredits = useCallback((credits) => {
    setUser((u) => (u ? { ...u, credits } : u));
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((u) => (u ? { ...u, ...patch } : u));
  }, []);

  const value = useMemo(
    () => ({ user, loading, loginWithData, logout, refresh, updateCredits, updateUser }),
    [user, loading, loginWithData, logout, refresh, updateCredits, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
